import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { normalizeTotpCode } from "@vital/shared";
import { useAuth } from "../context/AuthContext";
import { AuthBrand } from "../components/AuthBrand";
import { api, ApiError } from "../lib/api";

type EnrollState = {
  enrollmentToken: string;
  otpauthUri: string;
  secret: string;
};

export function MfaEnrollPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, refresh } = useAuth();
  const state = location.state as EnrollState | null;

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  useEffect(() => {
    if (state?.otpauthUri) {
      void QRCode.toDataURL(state.otpauthUri, { width: 200, margin: 1 }).then(setQrDataUrl);
    }
  }, [state?.otpauthUri]);

  if (!state?.enrollmentToken) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.mfaEnroll(state!.enrollmentToken, normalizeTotpCode(code));
      setUser(result.user);
      await refresh();
      setRecoveryCodes(result.recoveryCodes);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "کد تأیید نامعتبر است");
    } finally {
      setSubmitting(false);
    }
  }

  if (recoveryCodes) {
    return (
      <div className="auth-page">
        <AuthBrand />
        <div className="auth-card auth-card--wide">
          <h1>کدهای بازیابی</h1>
          <p className="auth-warning">
            این کدها را یک‌بار ذخیره کنید. پس از بستن این صفحه دیگر نمایش داده نمی‌شوند.
          </p>
          <ul className="recovery-codes">
            {recoveryCodes.map((c) => (
              <li key={c}>
                <bdi dir="ltr" lang="en" className="ltr-isolate latin-digits">
                  {c}
                </bdi>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn btn--primary btn--block"
            onClick={() => navigate("/", { replace: true })}
          >
            ادامه به داشبورد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <AuthBrand />
      <div className="auth-card auth-card--wide">
        <h1>فعال‌سازی MFA</h1>
        <p className="auth-subtitle">برنامهٔ احراز هویت را اسکن کنید یا کلید را دستی وارد کنید.</p>

        {qrDataUrl && (
          <div className="qr-wrap">
            <img src={qrDataUrl} alt="QR code for TOTP enrollment" width={200} height={200} />
          </div>
        )}

        <p className="secret-display">
          کلید:{" "}
          <bdi dir="ltr" lang="en" className="ltr-isolate latin-digits">
            {state.secret}
          </bdi>
        </p>

        <form onSubmit={handleSubmit} className="auth-form">
          <label>
            <span>کد ۶ رقمی</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              dir="ltr"
              lang="en"
              className="ltr-isolate latin-digits"
              value={code}
              onChange={(e) => setCode(normalizeTotpCode(e.target.value).slice(0, 6))}
              required
              autoComplete="one-time-code"
              placeholder="XXXXXX"
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
            {submitting ? "در حال تأیید…" : "تأیید و فعال‌سازی"}
          </button>
        </form>
      </div>
    </div>
  );
}
